import { $isCodeNode } from "@lexical/code";
import { HeadingNode } from "@lexical/rich-text";
import {
  $findMatchingParent,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  getNearestEditorFromDOMNode,
  KEY_DOWN_COMMAND,
  type LexicalEditor,
  type RangeSelection,
} from "lexical";
import { shallowRef } from "vue";

import { CommandId, Key } from "~/shared/enums";

const editorSelection = shallowRef<{
  editor: LexicalEditor;
  selection: RangeSelection;
  isInCode: boolean;
} | null>(null);

const INLINE_COMMANDS = new Set([
  CommandId.EDITOR_BOLD,
  CommandId.EDITOR_ITALICIZE,
  CommandId.EDITOR_UNDERLINE,
  CommandId.EDITOR_STRIKETHROUGH,
  CommandId.EDITOR_SET_CODE_SNIPPET,
  CommandId.EDITOR_ADD_LINK,
  CommandId.EDITOR_TEXT_OR_BG_COLOR,
]);

export const canRunEditorCommand = (commandId: CommandId) =>
  !!editorSelection.value && (!INLINE_COMMANDS.has(commandId) || !editorSelection.value.isInCode);

export const captureEditorSelection = () => {
  const editor = getNearestEditorFromDOMNode(document.activeElement);
  editorSelection.value = null;
  if (!editor?.isEditable() || !editor.hasNodes([HeadingNode])) {
    return;
  }
  editor.getEditorState().read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      editorSelection.value = {
        editor,
        selection,
        isInCode: selection.getNodes().every((node) => $findMatchingParent(node, $isCodeNode)),
      };
    }
  });
};

export const runEditorCommand = async (keys: Key[], isMac: boolean) => {
  const target = editorSelection.value;
  if (!target) {
    return;
  }
  // Restore the selection after the command center releases its focus trap.
  await new Promise(requestAnimationFrame);
  target.editor.getRootElement()?.focus({ preventScroll: true });
  target.editor.update(() => {
    $setSelection(target.selection.clone());
    const code = keys[keys.length - 1];
    const key = code.startsWith("Key") ? code.slice(3).toLowerCase() : code.replace("Digit", "");
    target.editor.dispatchCommand(
      KEY_DOWN_COMMAND,
      new KeyboardEvent("keydown", {
        key,
        code,
        metaKey: isMac && keys.includes(Key.Control),
        ctrlKey: !isMac && keys.includes(Key.Control),
        shiftKey: keys.includes(Key.Shift),
        altKey: keys.includes(Key.Alt),
        cancelable: true,
      })
    );
  });
};
